/**
 * Service to manage version checking and service worker registration
 */

interface VersionData {
  currentVersion: string;
  newVersion: string;
  timestamp: string;
}

type VersionUpdateCallback = (data: VersionData) => void;

class VersionCheckService {
  private registration: ServiceWorkerRegistration | null = null;
  private readonly callbacks: VersionUpdateCallback[] = [];

  /**
   * Initialize the version check service worker (production only)
   */
  async initialize(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return;
    }

    try {
      // Register the service worker
      this.registration = await navigator.serviceWorker.register(
        '/version-check-worker.js',
        { scope: '/' }
      );

      console.log('Version check service worker registered');

      // Listen for messages from the service worker
      navigator.serviceWorker.addEventListener('message', this.handleMessage);

      // Trigger an immediate version check
      this.checkVersion();
    } catch (error) {
      console.error('Failed to register version check service worker:', error);
    }
  }

  /**
   * Handle messages from the service worker
   */
  private readonly handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'VERSION_UPDATE') {
      console.log('New version available:', event.data);
      
      // Notify all registered callbacks
      this.callbacks.forEach(callback => callback(event.data));
    }
  };

  /**
   * Register a callback to be called when a new version is detected
   */
  onVersionUpdate(callback: VersionUpdateCallback): () => void {
    this.callbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Manually trigger a version check
   */
  checkVersion(): void {
    if (this.registration?.active) {
      this.registration.active.postMessage({ type: 'CHECK_VERSION' });
    }
  }

  /**
   * Reload the page to get the new version
   */
  reloadForUpdate(): void {
    (globalThis as any).location.reload();
  }
}

// Export singleton instance
export const versionCheckService = new VersionCheckService();
